import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { rentalService } from "../services/rental.service";
import type {
  RentalResponseDTO,
  RentalSearchParams,
} from "../types/rental.types";
import { RentalStatus, RentalType } from "@/shared/types/enums";

export interface RentalListFilters {
  startDate: string;
  endDate: string;
  rentalTypes?: RentalType[];
  statuses?: RentalStatus[];
  contractNumbers?: string[];
  rentalId?: number;
  sellerIds?: number[];
  clientName?: string;
}

interface UseRentalListOptions {
  initialFilters: RentalListFilters;
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 10;

export function useRentalList({
  initialFilters,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseRentalListOptions) {
  const [data, setData] = useState<RentalResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState<RentalListFilters>(initialFilters);
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (targetPage: number) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const pagination = {
          page: targetPage,
          size: pageSize,
          sortBy: "rentedAt",
          sortDirection: "DESC",
        } as const;
        // Busca por ID é exata: os demais filtros são omitidos para o
        // aluguel ser encontrado mesmo fora do período/status selecionados.
        // Busca por contrato omite só o período (o default de mês atual
        // esconderia contratos antigos); status/tipo seguem aplicáveis.
        const searchParams: RentalSearchParams = filters.rentalId
          ? { rentalId: filters.rentalId, ...pagination }
          : filters.contractNumbers?.length
            ? {
                contractNumbers: filters.contractNumbers,
                rentalTypes: filters.rentalTypes,
                statuses: filters.statuses,
                sellerIds: filters.sellerIds,
                clientName: filters.clientName,
                ...pagination,
              }
            : { ...filters, ...pagination };
        const response = await rentalService.search(searchParams);
        if (requestIdRef.current !== requestId) return;
        setData(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setPage(targetPage);
      } catch (err: unknown) {
        if (requestIdRef.current !== requestId) return;
        const message = getApiErrorMessage(err, "Erro ao carregar aluguéis");
        setError(message);
        toast.error(message);
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [filters, pageSize],
  );

  useEffect(() => {
    // load() dispara setLoading(true) síncrono para spinner imediato;
    // não causa cascading render porque loading não está nas deps.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(0);
  }, [load]);

  const removeLocal = useCallback((id: number) => {
    setData((prev) => prev.filter((item) => item.id !== id));
    setTotalElements((prev) => Math.max(0, prev - 1));
  }, []);

  const updateLocal = useCallback((updated: RentalResponseDTO) => {
    setData((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item)),
    );
  }, []);

  const reload = useCallback(() => {
    load(page);
  }, [load, page]);

  return {
    data,
    loading,
    error,
    page,
    totalPages,
    totalElements,
    filters,
    setFilters,
    setPage: load,
    reload,
    removeLocal,
    updateLocal,
  };
}
